import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler, getErrorMessage } from '@zero/shared/util-global-state';
import { OnboardingData } from '@zero/shared/util-models';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { OnboardingInfrastructure } from '../infrastructure/onboarding.infrastructure';
import { onboardingActions, onboardingApiActions } from './onboarding.actions';
import { onboardingFeature } from './onboarding.reducer';

@Injectable({ providedIn: 'root' })
export class OnboardingEffects {
  readonly #store = inject(Store);
  readonly #actions$ = inject(Actions);
  readonly #infra = inject(OnboardingInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public sendEmail$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendEmail),
      switchMap(({ email }) =>
        this.#infra.sendEmail(email).pipe(
          map((response) =>
            response.successful
              ? onboardingApiActions.sendEmailSuccess({ email })
              : onboardingApiActions.sendEmailFailure({ error: response.error }),
          ),
          catchError((error) => of(onboardingApiActions.sendEmailFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendConfirmationData$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendConfirmationData),
      switchMap(({ email, code, adAgreed }) =>
        this.#infra.sendConfirmationData(email, code, adAgreed).pipe(
          map(() => onboardingApiActions.sendConfirmationDataSuccess()),
          catchError((error) => of(onboardingApiActions.sendConfirmationDataFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public signUp$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.signUp),
      concatLatestFrom(() => this.#store.select(onboardingFeature.selectEmail)),
      switchMap(([{ password }, email]) =>
        this.#infra.signUp(email, password).pipe(
          map((response) =>
            response.success ? onboardingApiActions.signUpSuccess() : onboardingApiActions.signUpFailure({ error: response.errorMessage }),
          ),
          catchError((error) => of(onboardingApiActions.signUpFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public login$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.login),
      switchMap(({ email, password }) =>
        this.#infra.login(email, password).pipe(
          map(({ success, message }) =>
            success ? onboardingApiActions.loginSuccess() : onboardingApiActions.loginFailure({ error: message }),
          ),
          catchError((error) => of(onboardingApiActions.loginFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public triggerLoadOnboardingData$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingApiActions.loginSuccess),
      map(() => onboardingActions.loadOnboardingData()),
    );
  });

  public loadOnboardingData$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.loadOnboardingData),
      switchMap(() =>
        this.#infra.loadOnboardingData().pipe(
          map((onboardingData) => onboardingApiActions.loadOnboardingDataSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.loadOnboardingDataFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public triggerCheckPhoneNumberVerified$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingApiActions.loadOnboardingDataSuccess, onboardingApiActions.sendPhoneNumberSuccess),
      filter(({ onboardingData }) => !!onboardingData?.cMobileNr),
      map(() => onboardingActions.checkPhoneNumberVerified()),
    );
  });

  public checkPhoneNumberVerified$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.checkPhoneNumberVerified),
      switchMap(() =>
        this.#infra.checkPhoneNumberVerified().pipe(
          map((isPhoneNumberVerified) => onboardingApiActions.checkPhoneNumberVerifiedSuccess({ isPhoneNumberVerified })),
          catchError((error) => of(onboardingApiActions.checkPhoneNumberVerifiedFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendPhoneNumber$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendPhoneNumber),
      switchMap(({ phoneNumber }) =>
        this.#infra.sendPhoneNumber(phoneNumber).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendPhoneNumberSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendPhoneNumberFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public verifyCode$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.verifyCode),
      switchMap(({ code }) =>
        this.#infra.verifyCode(code).pipe(
          map((success) => {
            if (!success) {
              return onboardingApiActions.verifyCodeFailure({ error: 'Invalid code' });
            }
            return onboardingApiActions.verifyCodeSuccess();
          }),
          catchError((error) => of(onboardingApiActions.verifyCodeFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public resendCode$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.resendCode),
      switchMap(() =>
        this.#infra.resendCode().pipe(
          map(() => onboardingApiActions.resendCodeSuccess()),
          catchError((error) =>
            of(
              onboardingApiActions.resendCodeFailure({
                //hack to get the error message from the backend lambda
                error: getErrorMessage({
                  status: 503,
                  message: error?.message?.replace('CustomMessage failed with error ', ''),
                  errors: [error],
                }),
              }),
            ),
          ),
        ),
      ),
    );
  });

  public sendInitialDeposit$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendInitialDeposit),
      switchMap(({ amount }) =>
        this.#infra.sendInitialDeposit(amount).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendInitialDepositSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendInitialDepositFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendPersonalInformation$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendPersonalInformation),
      switchMap(({ personalInformation }) =>
        this.#infra.sendPersonalInformation(personalInformation).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendPersonalInformationSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendPersonalInformationFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendResidenceInformation$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendResidenceInformation),
      switchMap(({ residenceInformation }) =>
        this.#infra.sendResidenceInformation(residenceInformation).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendResidenceInformationSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendResidenceInformationFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendBankAccountInformation$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendBankAccountInformation),
      switchMap(({ bankAccountInformation }) =>
        this.#infra.sendBankAccountInformation(bankAccountInformation).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendBankAccountInformationSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendBankAccountInformationFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public loadBankByIban$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.loadBankByIban),
      switchMap(({ iban }) =>
        this.#infra.loadBankByIban(iban).pipe(
          map((bank) => onboardingApiActions.loadBankByIbanSuccess({ bank })),
          catchError((error) => of(onboardingApiActions.loadBankByIbanFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public loadBranches$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.loadBranches),
      switchMap(() =>
        this.#infra.loadBranches().pipe(
          map((branches) => onboardingApiActions.loadBranchesSuccess({ branches })),
          catchError((error) => of(onboardingApiActions.loadBranchesFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public sendNationalityTaxAndEmploymentInformation$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendNationalityTaxAndEmploymentInformation),
      switchMap(({ nationalityTaxAndEmploymentInformation }) =>
        this.#infra.sendNationalityTaxAndEmploymentInformation(nationalityTaxAndEmploymentInformation).pipe(
          map((onboardingData: OnboardingData) =>
            onboardingApiActions.sendNationalityTaxAndEmploymentInformationSuccess({ onboardingData }),
          ),
          catchError((error) =>
            of(onboardingApiActions.sendNationalityTaxAndEmploymentInformationFailure({ error: getErrorMessage(error) })),
          ),
        ),
      ),
    );
  });

  public sendProvisionInformation$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.sendProvisionInformation),
      switchMap(({ provisionInformation }) =>
        this.#infra.sendProvisionInformation(provisionInformation).pipe(
          map((onboardingData: OnboardingData) => onboardingApiActions.sendProvisionInformationSuccess({ onboardingData })),
          catchError((error) => of(onboardingApiActions.sendProvisionInformationFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public loadCountries$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.loadCountries),
      switchMap(() =>
        this.#infra.loadCountries().pipe(
          map((countries) => onboardingApiActions.loadCountriesSuccess({ countries })),
          catchError((error) => of(onboardingApiActions.loadCountriesFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public deleteAllData$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(onboardingActions.deleteAllData),
      switchMap(() =>
        this.#infra.deleteAllData().pipe(
          map(() => onboardingApiActions.deleteAllDataSuccess()),
          catchError((error) => of(onboardingApiActions.deleteAllDataFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          onboardingApiActions.sendEmailFailure,
          onboardingApiActions.signUpFailure,
          onboardingApiActions.loginFailure,
          onboardingApiActions.loadOnboardingDataFailure,
          onboardingApiActions.sendPhoneNumberFailure,
          onboardingApiActions.checkPhoneNumberVerifiedFailure,
          onboardingApiActions.verifyCodeFailure,
          onboardingApiActions.resendCodeFailure,
          onboardingApiActions.sendInitialDepositFailure,
          onboardingApiActions.sendPersonalInformationFailure,
          onboardingApiActions.sendResidenceInformationFailure,
          onboardingApiActions.loadBankByIbanFailure,
          onboardingApiActions.sendBankAccountInformationFailure,
          onboardingApiActions.loadBranchesFailure,
          onboardingApiActions.sendNationalityTaxAndEmploymentInformationFailure,
          onboardingApiActions.sendProvisionInformationFailure,
          onboardingApiActions.sendConfirmationDataFailure,
          onboardingApiActions.loadCountriesFailure,
          onboardingApiActions.deleteAllDataFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
